import { useContext } from "react";
import { authContext } from "@/shared/model/authContext";
import { useProject } from "@/entities/project/model/projectContext";
import { PERMISSIONS, PROJECT_PERMISSIONS } from "@/shared/config/permissions";
import { PROJECT_SECTION_PERMISSIONS } from "@/shared/config/projectSectionPermissions";
import type { ProjectSection } from "@/shared/types/project";

/**
 * Permissões agregadas de Obras, extraídas verbatim da lógica que estava inline
 * na sidebar. Fonte única para sidebar e páginas de detalhe.
 */
export interface ProjectPermissionFlags {
    canListProjects: boolean;
    canAccessProjectDetails: boolean;
    canAccessProjectPlanning: boolean;
    canAccessProjectRegistry: boolean;
    canAccessProjectFinancial: boolean;
    canAccessProjectPurchases: boolean;
    canAccessProjectTasks: boolean;
    canAccessProjectWorkers: boolean;
    canAccessProjectStock: boolean;
    canEditProjectPlanning: boolean;
    canViewProjectFinancialDetails: boolean;
    canExtractDocuments: boolean;
    /** Seções de acesso da obra (ver ProjectSection): sem elas o backend devolve os campos a null. */
    canViewProjectSchedule: boolean;
    canViewProjectTechnical: boolean;
    canViewProjectClient: boolean;
}

export function useProjectPermissions(): ProjectPermissionFlags {
    const { checkPermission } = useContext(authContext);
    const { projectPermissions } = useProject();

    const hasAnyPermission = (permission: string) =>
        checkPermission(permission) || projectPermissions.includes(permission);

    // Seções da obra (ver ProjectSection): sem elas o backend devolve os campos
    // da seção a null, então os blocos correspondentes não são mostrados.
    const hasSection = (section: ProjectSection) =>
        checkPermission(PROJECT_SECTION_PERMISSIONS[section].global) ||
        projectPermissions.includes(PROJECT_SECTION_PERMISSIONS[section].project);
    const hasFinancialSection = hasSection("FINANCIAL");

    const canListProjects =
        checkPermission(PERMISSIONS.CAN_LIST_PROJECTS) ||
        checkPermission(PERMISSIONS.CAN_LIST_PARTICIPATING_PROJECTS);

    return {
        canListProjects,
        canAccessProjectDetails: canListProjects,
        canAccessProjectPlanning:
            hasAnyPermission(PERMISSIONS.CAN_LIST_STEPS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_STEP),
        canAccessProjectRegistry:
            hasAnyPermission(PERMISSIONS.CAN_LIST_DIARIES) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_DIARY),
        canAccessProjectFinancial:
            hasAnyPermission(PERMISSIONS.CAN_LIST_COSTS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_OFFER_COSTS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_RECEIVEMENTS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_LIST_THIS_PROJECT_COST_STEP_COSTS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_OFFER) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_OFFER_COST) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_RECEIVEMENT),
        canAccessProjectPurchases:
            hasAnyPermission(PERMISSIONS.CAN_LIST_PURCHASE_ORDERS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_PURCHASE_ORDER_LISTS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_SOLICITATIONS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_LIST_THIS_PROJECT_PURCHASE_ORDER_LISTS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_PURCHASE_ORDER),
        canAccessProjectTasks:
            hasAnyPermission(PERMISSIONS.CAN_LIST_TASKS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_TASK) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_FINISH_THIS_PROJECT_TASK),
        canAccessProjectWorkers:
            hasAnyPermission(PERMISSIONS.CAN_LIST_PROJECT_WORKERS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_WORKER_PROFILE_IN_THIS_PROJECT),
        canAccessProjectStock:
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_GET_THIS_PROJECT_STOCK) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_STOCK_BALANCES),
        canEditProjectPlanning:
            hasAnyPermission(PERMISSIONS.CAN_UPDATE_STEP) ||
            hasAnyPermission(PERMISSIONS.CAN_CREATE_STEP) ||
            hasAnyPermission(PERMISSIONS.CAN_DELETE_STEP) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_STEP) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_DELETE_THIS_PROJECT_STEP) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_DISABLE_THIS_PROJECT_STEP),
        canViewProjectFinancialDetails: hasFinancialSection && (
            hasAnyPermission(PERMISSIONS.CAN_LIST_COSTS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_OFFER_COSTS) ||
            hasAnyPermission(PERMISSIONS.CAN_LIST_RECEIVEMENTS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_LIST_THIS_PROJECT_COST_STEP_COSTS) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_OFFER) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_OFFER_COST) ||
            projectPermissions.includes(PROJECT_PERMISSIONS.CAN_CREATE_THIS_PROJECT_RECEIVEMENT)),
        canViewProjectSchedule: hasSection("SCHEDULE"),
        canViewProjectTechnical: hasSection("TECHNICAL"),
        canViewProjectClient: hasSection("CLIENT"),
        canExtractDocuments: hasAnyPermission(PERMISSIONS.CAN_EXTRACT_DOCUMENTS),
    };
}
